"use client";

import { useState } from "react";
import { CheckCircle2, RotateCcw } from "lucide-react";
import type { VitrineFormConfig, VitrineFormField } from "@/lib/vitrine/types";

// Démo "formulaire métier" : un vrai formulaire, validé comme il le serait sur
// leur site, mais qui n'envoie RIEN (décision 2026-09-27) : l'écran de
// confirmation explique où arriverait la demande sur leur futur site.

type Values = Record<string, string | string[] | boolean>;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const TEL_RE = /^[+\d][\d\s.()-]{7,}$/;

function validate(fields: VitrineFormField[], values: Values): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const f of fields) {
    const v = values[f.id];
    const empty =
      v === undefined ||
      v === "" ||
      v === false ||
      (Array.isArray(v) && v.length === 0);
    if (f.required && empty) {
      errors[f.id] = f.type === "checkbox" && f.options.length === 0 ? "À cocher pour envoyer" : "Champ requis";
      continue;
    }
    if (typeof v === "string" && v) {
      if (f.type === "email" && !EMAIL_RE.test(v)) errors[f.id] = "Adresse e-mail invalide";
      if (f.type === "tel" && !TEL_RE.test(v)) errors[f.id] = "Numéro invalide";
    }
  }
  return errors;
}

const inputCls =
  "w-full rounded-md border border-line bg-paper-pure px-3 py-2 text-sm text-ink placeholder:text-ink/40 focus:border-gold focus:outline-none";

export function DemoForm({
  config,
  onInteract,
  onComplete,
}: {
  config: VitrineFormConfig;
  onInteract: () => void;
  onComplete: () => void;
}) {
  const [values, setValues] = useState<Values>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [sent, setSent] = useState(false);

  const set = (id: string, v: Values[string]) => {
    onInteract();
    setValues((prev) => ({ ...prev, [id]: v }));
    setErrors((prev) => {
      if (!prev[id]) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    onInteract();
    const errs = validate(config.fields, values);
    setErrors(errs);
    if (Object.keys(errs).length === 0) {
      setSent(true);
      onComplete();
    }
  };

  if (sent) {
    return (
      <div className="flex flex-col items-center py-10 text-center">
        <CheckCircle2 className="h-10 w-10 text-gold" strokeWidth={1.6} />
        <p className="mt-4 max-w-md font-display text-xl text-ink">{config.confirmation}</p>
        <p className="mt-4 max-w-md text-sm text-ink/60">
          Démonstration : rien n&apos;a été envoyé. Sur votre site, cette demande arriverait dans votre
          boîte e-mail et dans votre espace d&apos;administration.
        </p>
        <button
          type="button"
          onClick={() => {
            setValues({});
            setSent(false);
          }}
          className="mt-6 inline-flex items-center gap-1.5 text-sm text-ink/60 underline underline-offset-4 hover:text-ink"
        >
          <RotateCcw className="h-3.5 w-3.5" />
          Recommencer
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate className="space-y-4">
      <div>
        <h3 className="font-display text-2xl text-ink">{config.titre}</h3>
        {config.intro && <p className="mt-1 text-sm text-ink/60">{config.intro}</p>}
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        {config.fields.map((f) => {
          const wide = f.type === "textarea" || f.type === "radio" || f.type === "checkbox";
          const err = errors[f.id];
          const label = (
            <span className="mb-1 block text-sm font-medium text-ink/80">
              {f.label}
              {f.required && <span className="text-gold"> *</span>}
            </span>
          );
          let control: React.ReactNode;
          if (f.type === "textarea") {
            control = (
              <textarea
                rows={3}
                className={inputCls}
                placeholder={f.placeholder}
                value={(values[f.id] as string) ?? ""}
                onChange={(e) => set(f.id, e.target.value)}
              />
            );
          } else if (f.type === "select") {
            control = (
              <select
                className={inputCls}
                value={(values[f.id] as string) ?? ""}
                onChange={(e) => set(f.id, e.target.value)}
              >
                <option value="">Choisir…</option>
                {f.options.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            );
          } else if (f.type === "radio") {
            control = (
              <div className="flex flex-wrap gap-2">
                {f.options.map((o) => {
                  const on = values[f.id] === o;
                  return (
                    <button
                      key={o}
                      type="button"
                      onClick={() => set(f.id, o)}
                      aria-pressed={on}
                      className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${
                        on ? "border-gold bg-gold/10 text-ink" : "border-line text-ink/70 hover:border-ink/30"
                      }`}
                    >
                      {o}
                    </button>
                  );
                })}
              </div>
            );
          } else if (f.type === "checkbox" && f.options.length > 0) {
            const list = (values[f.id] as string[]) ?? [];
            control = (
              <div className="flex flex-wrap gap-x-5 gap-y-2">
                {f.options.map((o) => (
                  <label key={o} className="flex items-center gap-2 text-sm text-ink/80">
                    <input
                      type="checkbox"
                      checked={list.includes(o)}
                      onChange={(e) => set(f.id, e.target.checked ? [...list, o] : list.filter((x) => x !== o))}
                    />
                    {o}
                  </label>
                ))}
              </div>
            );
          } else if (f.type === "checkbox") {
            return (
              <label key={f.id} className="flex items-start gap-2 text-sm text-ink/70 sm:col-span-2">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={values[f.id] === true}
                  onChange={(e) => set(f.id, e.target.checked)}
                />
                <span>
                  {f.label}
                  {f.required && <span className="text-gold"> *</span>}
                  {err && <span className="block text-xs text-red-700">{err}</span>}
                </span>
              </label>
            );
          } else {
            control = (
              <input
                type={f.type}
                className={inputCls}
                placeholder={f.placeholder}
                value={(values[f.id] as string) ?? ""}
                onChange={(e) => set(f.id, e.target.value)}
              />
            );
          }
          return (
            <label key={f.id} className={`block ${wide ? "sm:col-span-2" : ""}`}>
              {label}
              {control}
              {err && <span className="mt-1 block text-xs text-red-700">{err}</span>}
            </label>
          );
        })}
      </div>
      <button
        type="submit"
        className="rounded-full bg-navy px-6 py-2.5 text-sm font-medium text-paper transition-colors hover:bg-navy-deep"
      >
        {config.submitLabel}
      </button>
    </form>
  );
}
